import { SysConst as SC } from '@/trials/plugins/const';
import round from '@/trials/lib/round';

/**
 * ナレッジチャンクを保持するクラス
 */
export default class KnowledgeChunks {
    /**
     * 生データ
     * 最後に作成完了したWLモデルのナレッジチャンク取得APIなど
     * ナレッジチャンクを返すAPIのレスポンスを想定しています。
     */
    private _rawData: any[] = [];

    /**
     * 生データのヘッダー
     */
    private _headers: any[] = [];

    /**
     * 各POSデータを配列で保持
     */
    private _posArrays: any[] = [];

    /**
     * 各POSデータをオブジェクトで保持
     */
    private _posObjects: any[] = [];

    /**
     * 各NEGデータを配列で保持
     */
    private _negArrays: any[] = [];

    /**
     * 各NEGデータをオブジェクトで保持
     */
    private _negObjects: any[] = [];

    /**
     * weightが0ではない各データを配列で保持
     */
    private _weightNotZeroArrays: any[] = [];

    /**
     * weightが0ではない各データをオブジェクトで保持
     */
    private _weightNotZeroObjects: any[] = [];

    /**
     * 保持するPOSデータの最大数
     */
    private _posLimit: number = SC.OutputData.KC.Limit.POS;

    /**
     * 保持するNEGデータの最大数
     */
    private _negLimit: number = SC.OutputData.KC.Limit.NEG;

    /**
     * ヘッダーの値をindexに変換する用のオブジェクト
     */
    private _headerToindex: { [key: string]: number } = {};

    /**
     * getter ヘッダー取得
     */
    public get headers(): any[] {
        return [...this._headers];
    }

    /**
     * getter 各POSデータが配列になっているリストを取得
     */
    public get posArrays(): any[] {
        return [...this._posArrays];
    }

    /**
     * getter 各NEGデータが配列になっているリストを取得
     */
    public get negArrays(): any[] {
        return [...this._negArrays];
    }

    /**
     * getter weightが0ではないデータを配列のリストで取得
     */
    public get weightNotZeroArrays(): any[] {
        return [...this._weightNotZeroArrays];
    }

    /**
     * getter 各POSデータがオブジェクトになっているリストを取得
     */
    public get posObjects(): any[] {
        return [...this._posObjects];
    }

    /**
     * getter 各NEGデータがオブジェクトになっているリストを取得
     */
    public get negObjects(): any[] {
        return [...this._negObjects];
    }

    /**
     * getter weightが0ではないデータをオブジェクトのリストで取得
     */
    public get weightNotZeroObjects(): any[] {
        return [...this._weightNotZeroObjects];
    }

    /**
     * コンストラクタ
     * @param rawData 生データ
     */
    constructor(rawData: any[]) {
        this._rawData = rawData;
        this._headers = rawData.slice(0, 1)[0];

        for (let i = 0; i < this._headers.length; i++) {
            const header = this._headers[i];
            this._headerToindex[header] = i;
        }

        this._separatePosNeg();
    }

    /**
     * POS / NEG のデータに分類する
     */
    private _separatePosNeg() {
        const weightNotZeroArrays: any[] = [];
        const weightNotZeroObjects: any[] = [];
        const posArrays: any[] = [];
        const posObjects: any[] = [];
        const negArrays: any[] = [];
        const negObjects: any[] = [];

        const weightIndex = this._headerToindex['weight'];
        const labelIndex = this._headerToindex['label'];
        const nposIndex = this._headerToindex['npos'];
        const nnegIndex = this._headerToindex['nneg'];

        const dataList = this._rawData.slice(1);
        // weightの昇順に並び替える
        const sortedData = dataList.sort((a, b) => {
            if (a[weightIndex] <= b[weightIndex]) {
                return -1;
            } else {
                return 1;
            }
        });

        posArrays.push(this._headers);
        negArrays.push(this._headers);
        weightNotZeroArrays.push(this._headers);

        for (let i = 0; i < sortedData.length; i++) {
            const rawData: any[] = sortedData[i];
            const chunk: { [key: string]: any } = {};

            // weightが0のデータは省く
            if (rawData[weightIndex] == 0) continue;

            // 数値の場合、少数第3位を四捨五入する
            const data: any[] = [];
            for (let k = 0; k < rawData.length; k++) {
                const val = rawData[k];
                // 数値ではない判定
                if (isNaN(val) || val === true || val === false || val === null) {
                    data.push(val);
                } else {
                    data.push(round(val, 2));
                }
            }

            // labelがfalseの場合、npos と nneg の値を入れ替える
            if (rawData[labelIndex] === false) {
                const npos = data[nposIndex];
                data[nposIndex] = data[nnegIndex];
                data[nnegIndex] = npos;
            }

            // { key1: val1, key2: val2 ... }のオブジェクトに変換
            for (let j = 0; j < this._headers.length; j++) {
                const header = this._headers[j];
                chunk[header] = data[j];
            }

            weightNotZeroArrays.push(data);
            weightNotZeroObjects.push(chunk);
            if (chunk.weight > 0) {
                // 元のデータをweightの昇順に並べているので、
                // weight > 0 のデータは先頭に追加して、最終的にweight降順にする
                posArrays.unshift(data);
                posObjects.unshift(chunk);
            } else {
                negArrays.push(data);
                negObjects.push(chunk);
            }
        }

        this._weightNotZeroArrays = weightNotZeroArrays;
        this._weightNotZeroObjects = weightNotZeroObjects;

        // POS / NEG を 上位データのみにする
        this._posArrays = posArrays.slice(0, this._posLimit);
        this._posObjects = posObjects.slice(0, this._posLimit);
        this._negArrays = negArrays.slice(0, this._negLimit);
        this._negObjects = negObjects.slice(0, this._negLimit);
    }
}
